// src/pages/ScreenshotUpload.jsx — Screenshot Ingestion UI
// Drag & drop PNG/JPG → Vision API → Pattern + Metrics display

import { useRef, useState } from "react";
import SectionTitle from "../components/ui/SectionTitle";
import { BASE_URL }  from "../api/workerClient";

const mono = { fontFamily: "var(--font-mono)" };

const badge = (color, bg) => ({
  display: "inline-flex", alignItems: "center",
  padding: "2px 10px", borderRadius: "var(--radius-sm)",
  background: bg, border: `1px solid ${color}30`,
  fontFamily: "var(--font-mono)", fontSize: 10, fontWeight: 700,
  letterSpacing: "0.1em", color, textTransform: "uppercase",
});

const PATTERN_COLORS = {
  CAMPAIGN_STRUCTURE_ERROR: "var(--warning)",
  TRACKING_BREAK:           "var(--danger)",
  BID_STRATEGY_FAILURE:     "#e06c00",
  ALGORITHM_CHANGE:         "var(--accent-primary)",
  PAYMENT_ACCOUNT_ISSUE:    "var(--danger)",
  PLATFORM_DEPRECATION:     "var(--text-muted)",
  OFFER_MARKET_MISMATCH:    "var(--text-secondary)",
  SCALING_INSTABILITY:      "var(--warning)",
};

const PATTERN_LABELS = {
  CAMPAIGN_STRUCTURE_ERROR: {
    label: "Campaigns competing with each other",
    description: "Campaign structure is causing internal competition",
  },
  TRACKING_BREAK: {
    label: "Conversion tracking broken",
    description: "Your ads ran but conversions stopped being recorded",
  },
  BID_STRATEGY_FAILURE: {
    label: "Bid strategy lost signal",
    description: "Your bidding algorithm stopped optimizing effectively",
  },
  ALGORITHM_CHANGE: {
    label: "External algorithm shift",
    description: "Google changed something — not your campaign's fault",
  },
  PAYMENT_ACCOUNT_ISSUE: {
    label: "Account or billing issue",
    description: "Something stopped your ads from running at account level",
  },
  PLATFORM_DEPRECATION: {
    label: "Feature or format deprecated",
    description: "A Google Ads feature your campaign relied on was changed",
  },
  OFFER_MARKET_MISMATCH: {
    label: "Offer-market mismatch",
    description: "The offer no longer matches what the market wants",
  },
  SCALING_INSTABILITY: {
    label: "Scaling instability",
    description: "Performance broke when budget or volume increased",
  },
};

function ConfBar({ value }) {
  const pct   = Math.round((value ?? 0) * 100);
  const color = pct >= 80 ? "var(--success)" : pct >= 60 ? "var(--warning)" : "var(--danger)";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <div style={{ flex: 1, height: 6, borderRadius: 4, background: "var(--bg-tertiary)", overflow: "hidden" }}>
        <div style={{ width: `${pct}%`, height: "100%", background: color, transition: "width 0.4s" }} />
      </div>
      <span style={{ ...mono, fontSize: 11, color, minWidth: 36, textAlign: "right" }}>{pct}%</span>
    </div>
  );
}

function MetricRow({ label, value, conf }) {
  if (value === null || value === undefined) return null;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "6px 0", borderBottom: "1px solid var(--border-subtle)" }}>
      <span style={{ ...mono, fontSize: 10, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.1em", minWidth: 100 }}>{label}</span>
      <span style={{ ...mono, fontSize: 13, color: "var(--text-primary)", fontWeight: 600 }}>{value}</span>
      {conf != null && <ConfBar value={conf} />}
    </div>
  );
}

function ResultPanel({ result }) {
  const patternColor = PATTERN_COLORS[result.candidate_pattern] ?? "var(--text-secondary)";
  const m = result.metrics ?? {};

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

      {/* Classification */}
      <div style={{ padding: "16px 20px", background: "var(--bg-card)", border: "1px solid var(--border-subtle)", borderRadius: "var(--radius)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
          <span style={{ ...mono, fontSize: 10, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.12em" }}>Screenshot type</span>
          <span style={badge("var(--accent-primary)", "var(--accent-faint)")}>{result.screenshot_type ?? "unknown"}</span>
        </div>
        <ConfBar value={result.classification?.confidence} />
        {result.classification?.reason && (
          <div style={{ ...mono, fontSize: 11, color: "var(--text-tertiary)", marginTop: 8 }}>{result.classification.reason}</div>
        )}
      </div>

      {/* Candidate Pattern */}
      {result.candidate_pattern && (
        <div style={{ padding: "16px 20px", background: "var(--bg-card)", border: `1px solid ${patternColor}40`, borderRadius: "var(--radius)", borderLeft: `3px solid ${patternColor}` }}>
          <div style={{ ...mono, fontSize: 9, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.14em", marginBottom: 6 }}>Detected pattern</div>
          <div style={{ ...mono, fontSize: 16, fontWeight: 700, color: patternColor }}>
            {PATTERN_LABELS[result.candidate_pattern]?.label ?? result.candidate_pattern}
          </div>
          {PATTERN_LABELS[result.candidate_pattern]?.description && (
            <div style={{ ...mono, fontSize: 11, color: "var(--text-secondary)", marginTop: 4 }}>
              {PATTERN_LABELS[result.candidate_pattern].description}
            </div>
          )}
          <div style={{ ...mono, fontSize: 9, color: "var(--text-muted)", marginTop: 6, letterSpacing: "0.1em" }}>
            {result.candidate_pattern}
          </div>
        </div>
      )}

      {/* Needs manual review */}
      {result.needs_manual_review && (
        <div style={{ padding: "12px 16px", background: "var(--warning-bg)", border: "1px solid var(--warning-border)", borderRadius: "var(--radius)" }}>
          <div style={{ ...mono, fontSize: 12, color: "var(--warning)", fontWeight: 600 }}>⚠ Screenshot unclear — manual review needed</div>
          <div style={{ ...mono, fontSize: 11, color: "var(--text-tertiary)", marginTop: 4 }}>Classification confidence too low. Try a higher-quality screenshot or upload CSV instead.</div>
        </div>
      )}

      {/* CSV Required — upsell */}
      {result.csv_required && (
        <div style={{ padding: "20px 24px", background: "var(--accent-faint)", border: "1px solid var(--accent-primary)", borderRadius: "var(--radius)" }}>
          <div style={{ ...mono, fontSize: 12, color: "var(--accent-primary)", fontWeight: 700, marginBottom: 8 }}>
            Ready for the full picture?
          </div>
          <div style={{ ...mono, fontSize: 11, color: "var(--text-secondary)", marginBottom: 6, lineHeight: 1.7 }}>
            Screenshot shows the pattern. A CSV analysis gives you:
          </div>
          <div style={{ ...mono, fontSize: 11, color: "var(--text-secondary)", marginBottom: 14, lineHeight: 2 }}>
            · The exact date performance started to change<br />
            · Estimated money lost<br />
            · What to fix and in what order
          </div>
          {result.csv_reason && (
            <div style={{ ...mono, fontSize: 10, color: "var(--text-muted)", marginBottom: 14 }}>{result.csv_reason}</div>
          )}
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <a href="/analyze" style={{
              display: "inline-flex", alignItems: "center",
              padding: "9px 18px", borderRadius: "var(--radius-sm)",
              background: "var(--accent-primary)", color: "#fff",
              fontFamily: "var(--font-mono)", fontSize: 11, fontWeight: 700,
              textDecoration: "none",
            }}>
              Run full CSV analysis →
            </a>
            <a href="mailto:garrytret@gmail.com?subject=Full Audit Request" style={{
              display: "inline-flex", alignItems: "center",
              padding: "9px 18px", borderRadius: "var(--radius-sm)",
              background: "var(--bg-elevated)", color: "var(--text-secondary)",
              fontFamily: "var(--font-mono)", fontSize: 11, fontWeight: 700,
              textDecoration: "none", border: "1px solid var(--border-subtle)",
            }}>
              Get full audit — €49 →
            </a>
          </div>
        </div>
      )}

      {/* Metrics */}
      {Object.keys(m).some(k => m[k]?.value != null) && (
        <div style={{ background: "var(--bg-card)", border: "1px solid var(--border-subtle)", borderRadius: "var(--radius)", padding: "16px 20px" }}>
          <div style={{ ...mono, fontSize: 9, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.14em", marginBottom: 12 }}>Extracted metrics</div>
          {m.spend?.value       != null && <MetricRow label="Spend"       value={`${result.currency ?? ""}${m.spend.value}`}       conf={m.spend?.confidence} />}
          {m.clicks?.value      != null && <MetricRow label="Clicks"      value={m.clicks.value}      conf={m.clicks?.confidence} />}
          {m.conversions?.value != null && <MetricRow label="Conversions" value={m.conversions.value} conf={m.conversions?.confidence} />}
          {m.ctr?.value         != null && <MetricRow label="CTR"         value={`${m.ctr.value}%`}   conf={m.ctr?.confidence} />}
          {m.cpc?.value         != null && <MetricRow label="Avg CPC"     value={`${result.currency ?? ""}${m.cpc.value}`} conf={m.cpc?.confidence} />}
        </div>
      )}

      {/* Signals */}
      {result.bid_strategy_signals?.length > 0 && (
        <div style={{ background: "var(--bg-card)", border: "1px solid var(--border-subtle)", borderRadius: "var(--radius)", padding: "16px 20px" }}>
          <div style={{ ...mono, fontSize: 9, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.14em", marginBottom: 12 }}>Bid strategy signals</div>
          {result.bid_strategy_signals.map((s, i) => (
            <div key={i} style={{ ...mono, fontSize: 12, color: "var(--warning)", padding: "4px 0", borderBottom: "1px solid var(--border-subtle)" }}>⚡ {s}</div>
          ))}
          {result.divergence_pct != null && (
            <div style={{ ...mono, fontSize: 12, color: "var(--danger)", marginTop: 8 }}>
              Divergence: <strong>{result.divergence_pct > 0 ? "+" : ""}{result.divergence_pct}%</strong> (actual vs target)
            </div>
          )}
        </div>
      )}

      {/* Wasted spend terms */}
      {result.wasted_spend_terms?.length > 0 && (
        <div style={{ background: "var(--bg-card)", border: "1px solid var(--border-subtle)", borderRadius: "var(--radius)", padding: "16px 20px" }}>
          <div style={{ ...mono, fontSize: 9, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.14em", marginBottom: 12 }}>
            Wasted spend terms ({result.wasted_spend_terms.length})
          </div>
          {result.wasted_spend_terms.slice(0, 8).map((t, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", borderBottom: "1px solid var(--border-subtle)" }}>
              <span style={{ ...mono, fontSize: 11, color: "var(--text-primary)" }}>{t.query}</span>
              <span style={{ ...mono, fontSize: 11, color: "var(--danger)" }}>
                {result.currency ?? ""}{t.spend?.value ?? "?"} spend · 0 conv
              </span>
            </div>
          ))}
        </div>
      )}

      {/* CTA — next step */}
      <div style={{
        padding: "20px 24px",
        borderRadius: "var(--radius)",
        background: "var(--accent-faint)",
        border: "1px solid var(--accent-primary)",
        display: "flex",
        flexDirection: "column",
        gap: 12,
      }}>
        <div style={{ ...mono, fontSize: 12, fontWeight: 700, color: "var(--text-primary)" }}>
          Want to know what to do next?
        </div>
        <div style={{ ...mono, fontSize: 11, color: "var(--text-secondary)", lineHeight: 1.6 }}>
          This pattern has a specific fix sequence. Send us your data — we'll walk through it within 24 hours.
        </div>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <a
            href="mailto:garrytret@gmail.com?subject=VIRAJ Screenshot Audit"
            style={{
              display: "inline-flex", alignItems: "center",
              padding: "8px 16px", borderRadius: "var(--radius-sm)",
              background: "var(--accent-primary)", color: "#fff",
              fontFamily: "var(--font-mono)", fontSize: 11, fontWeight: 700,
              textDecoration: "none", border: "none",
            }}
          >
            Email us →
          </a>
          <a
            href="mailto:garrytret@gmail.com?subject=Full Audit — €49"
            style={{
              display: "inline-flex", alignItems: "center",
              padding: "8px 16px", borderRadius: "var(--radius-sm)",
              background: "var(--bg-elevated)", color: "var(--text-secondary)",
              fontFamily: "var(--font-mono)", fontSize: 11, fontWeight: 700,
              textDecoration: "none", border: "1px solid var(--border-subtle)",
            }}
          >
            Get full audit — €49 →
          </a>
        </div>
      </div>

      {/* Case ID — collapsible */}
      <details style={{ marginTop: 4 }}>
        <summary style={{ ...mono, fontSize: 9, color: "var(--text-muted)", cursor: "pointer", letterSpacing: "0.1em" }}>
          Technical details
        </summary>
        <div style={{ ...mono, fontSize: 9, color: "var(--text-muted)", marginTop: 6, lineHeight: 1.8 }}>
          case_id: {result.case_id}<br />
          ingested: {result.ingested_at?.slice(0, 16)}
        </div>
      </details>
    </div>
  );
}

export default function ScreenshotUpload() {
  const fileRef   = useRef(null);
  const [dragging, setDragging]  = useState(false);
  const [loading,  setLoading]   = useState(false);
  const [result,   setResult]    = useState(null);
  const [error,    setError]     = useState(null);
  const [preview,  setPreview]   = useState(null); // data URL for thumbnail
  const [hintType, setHintType]  = useState("auto");

  const HINT_OPTIONS = [
    { value: "auto",         label: "Auto-detect" },
    { value: "campaign_table", label: "Campaign Table" },
    { value: "search_terms",   label: "Search Terms" },
    { value: "bid_strategy",   label: "Bid Strategy" },
  ];

  const reset = () => {
    setResult(null);
    setError(null);
    setPreview(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleFile = async (file) => {
    if (!file) return;
    const allowed = ["image/png", "image/jpeg", "image/webp"];
    if (!allowed.includes(file.type)) {
      setError("Please upload a PNG, JPG, or WEBP file.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError("File too large. Max 5 MB.");
      return;
    }

    // Show preview thumbnail
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target.result);
    reader.readAsDataURL(file);

    setError(null);
    setResult(null);
    setLoading(true);

    try {
      const form = new FormData();
      form.append("file", file);
      const typeParam = hintType !== "auto" ? `?type=${hintType}` : "";
      const res = await fetch(`${BASE_URL}/api/ingest/screenshot${typeParam}`, {
        method: "POST",
        body: form,
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error ?? data.msg ?? "API error");
      setResult(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const dropZone = {
    border:       `2px dashed ${dragging ? "var(--accent-primary)" : "var(--border-subtle)"}`,
    borderRadius: "var(--radius)",
    padding:      "48px 24px",
    textAlign:    "center",
    cursor:       "pointer",
    transition:   "all var(--t-fast)",
    background:   dragging ? "var(--accent-faint)" : "var(--bg-card)",
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 28 }}>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
        <div>
          <SectionTitle title="Screenshot Upload" />
          <div style={{ fontSize: 13, color: "var(--text-tertiary)", marginTop: 4, maxWidth: 520, lineHeight: 1.6 }}>
            Upload a Google Ads screenshot. VIRAJ detects the pattern and extracts metrics in one Vision API call.
          </div>
        </div>
        <span style={badge("var(--accent-primary)", "var(--accent-faint)")}>Vision v1.0</span>
      </div>

      {/* Screenshot type hint */}
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <span style={{ ...mono, fontSize: 10, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.1em" }}>Screen type</span>
        <div style={{ display: "flex", gap: 6 }}>
          {HINT_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={() => setHintType(opt.value)}
              style={{
                ...mono, fontSize: 10, padding: "5px 12px",
                borderRadius: "var(--radius-sm)",
                border: hintType === opt.value ? "1px solid var(--accent-primary)" : "1px solid var(--border-subtle)",
                background: hintType === opt.value ? "var(--accent-faint)" : "var(--bg-card)",
                color: hintType === opt.value ? "var(--accent-primary)" : "var(--text-muted)",
                cursor: "pointer", letterSpacing: "0.06em",
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Drop zone */}
      {!result && (
        <div
          style={dropZone}
          onClick={() => fileRef.current?.click()}
          onDragOver={e => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={e => { e.preventDefault(); setDragging(false); handleFile(e.dataTransfer.files[0]); }}
        >
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            style={{ display: "none" }}
            onChange={e => handleFile(e.target.files[0])}
          />
          {loading ? (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
              {preview && <img src={preview} alt="" style={{ maxHeight: 120, maxWidth: "100%", borderRadius: "var(--radius-sm)", opacity: 0.7 }} />}
              <div style={{ ...mono, fontSize: 13, color: "var(--accent-primary)" }}>Analyzing with Vision API…</div>
              <div style={{ ...mono, fontSize: 10, color: "var(--text-muted)" }}>Claude Haiku · usually 3–6 seconds</div>
            </div>
          ) : (
            <>
              <div style={{ fontSize: 32, marginBottom: 10, opacity: 0.35 }}>🖼</div>
              <div style={{ fontSize: 14, color: "var(--text-secondary)", marginBottom: 4 }}>
                Drop a screenshot here, or click to browse
              </div>
              <div style={{ ...mono, fontSize: 11, color: "var(--text-muted)" }}>
                PNG · JPG · WEBP · max 5 MB
              </div>
              <div style={{ ...mono, fontSize: 10, color: "var(--text-tertiary)", marginTop: 8 }}>
                Campaign Table · Search Terms Report · Bid Strategy Panel
              </div>
            </>
          )}
        </div>
      )}

      {/* Error */}
      {error && (
        <div style={{ ...mono, fontSize: 12, color: "var(--danger)", padding: "12px 16px", background: "var(--danger-bg)", borderRadius: "var(--radius)", border: "1px solid var(--danger-border)" }}>
          ✗ {error}
        </div>
      )}

      {/* Result */}
      {result && (
        <>
          <ResultPanel result={result} />
          <button
            onClick={reset}
            style={{ alignSelf: "flex-start", padding: "8px 20px", borderRadius: "var(--radius)", background: "var(--bg-card)", border: "1px solid var(--border-subtle)", cursor: "pointer", ...mono, fontSize: 11, color: "var(--text-secondary)" }}
          >
            ← UPLOAD ANOTHER
          </button>
        </>
      )}
    </div>
  );
}
